import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Phone, ClipboardList, ArrowLeft, CheckCircle2,
  User, Smartphone, Calendar, Users, Package, Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackLead } from "../lib/analytics";

export interface BookingInfo {
  title: string;
  subtitle?: string;
  badge?: string;
  badgeClass?: string;
}

interface Props {
  info: BookingInfo | null;
  onClose: () => void;
}

type Step = "options" | "form" | "success";

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

/* ── Animation variants (modal lifecycle only) ───────────── */

const backdrop = {
  hidden: { opacity: 0 },
  show:   { opacity: 1, transition: { duration: 0.2 } },
  exit:   { opacity: 0, transition: { duration: 0.15 } },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  show:   { opacity: 1, scale: 1, y: 0, transition: { type: "spring" as const, damping: 25, stiffness: 300 } },
  exit:   { opacity: 0, scale: 0.97, y: 10, transition: { duration: 0.15 } },
};

const slideIn = {
  hidden: { opacity: 0, x: 30 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.25, ease: "easeOut" as const } },
  exit:   { opacity: 0, x: -20, transition: { duration: 0.15 } },
};

/* ── Component ───────────────────────────────────────────── */

const BookingModal: React.FC<Props> = ({ info, onClose }) => {
  const { t } = useTranslation();
  const [step,    setStep]    = useState<Step>("options");
  const [form,    setForm]    = useState({
    name: "", contact: "", month: "", passengers: "1",
    packageService: info ? info.title + (info.subtitle ? ` — ${info.subtitle}` : "") : "",
  });
  const [errors,  setErrors]  = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = t("bookingModal.errors.name");
    if (!/^[0-9]{10}$/.test(form.contact)) e.contact = t("bookingModal.errors.contact");
    if (!form.month) e.month = t("bookingModal.errors.month");
    if (!form.passengers || Number(form.passengers) < 1) e.passengers = t("bookingModal.errors.passengers");
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSending(true);
    setSendErr(null);

    try {
      const packageName = form.packageService.trim() || t("bookingModal.generalInquiry");

      const res = await fetch("/api/submit-inquiry", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:         form.name.trim(),
          phone:        form.contact,
          travel_month: form.month,
          passengers:   form.passengers,
          package:      packageName,
        }),
      });

      if (res.ok) {
        trackLead(packageName);
        setStep("success");
      } else {
        setSendErr(t("bookingModal.errors.submitFailed"));
      }
    } catch {
      setSendErr(t("bookingModal.errors.network"));
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400 transition-colors duration-200 placeholder:text-gray-400";
  const labelClass = "flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      variants={backdrop}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <div className="absolute inset-0 bg-black/60" />
      <motion.div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden text-gray-800"
        onClick={(e) => e.stopPropagation()}
        variants={modal}
        initial="hidden"
        animate="show"
        exit="exit"
      >
        {/* ── Gradient Header ────────────────────────────── */}
        <div className="relative overflow-hidden" style={{
          background: "linear-gradient(135deg,#b45309 0%,#f59e0b 50%,#fbbf24 100%)",
          padding: "20px 24px",
        }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full"
            style={{ background: "rgba(255,255,255,0.08)" }} />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full"
            style={{ background: "rgba(255,255,255,0.06)" }} />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer smt-btn-close"
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "white" }}
          >
            <X size={16} strokeWidth={2.5} />
          </button>

          {info?.badge && (
            <span className={`text-xs font-bold px-3 py-0.5 rounded-full ${info.badgeClass}`}>
              {info.badge}
            </span>
          )}
          <h2 className="font-serif text-xl font-extrabold mt-1 pr-8" style={{ color: "#431407" }}>
            {info?.title ?? t("bookingModal.defaultTitle")}
          </h2>
          {info?.subtitle && (
            <p className="text-sm mt-1" style={{ color: "rgba(120,53,15,0.7)" }}>{info.subtitle}</p>
          )}
        </div>

        {/* ── Step content ───────────────────────────────── */}
        <AnimatePresence mode="wait">
          {/* Step 1 – Options */}
          {step === "options" && (
            <motion.div
              key="options"
              variants={slideIn}
              initial="hidden"
              animate="show"
              exit="exit"
              className="p-6 space-y-3"
            >
              <p className="text-gray-400 text-sm text-center mb-1">{t("bookingModal.howToProceed")}</p>

              <a
                href="tel:+918866077880"
                className="flex items-center gap-4 w-full cursor-pointer rounded-xl smt-option-btn"
                style={{
                  padding: "16px 20px", textDecoration: "none",
                  background: "linear-gradient(135deg,#22c55e,#16a34a)",
                  color: "white", fontWeight: 700,
                  boxShadow: "0 4px 14px rgba(34,197,94,0.25)",
                }}
              >
                <div className="flex items-center justify-center rounded-lg shrink-0"
                  style={{ width: 42, height: 42, background: "rgba(255,255,255,0.18)" }}>
                  <Phone size={20} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[15px]">{t("bookingModal.callUsNow")}</div>
                  <div className="text-xs font-normal" style={{ color: "rgba(255,255,255,0.75)" }}>
                    +91 88660 77880 / +91 94265 87801
                  </div>
                </div>
              </a>

              <button
                onClick={() => setStep("form")}
                className="flex items-center gap-4 w-full cursor-pointer rounded-xl smt-option-btn"
                style={{
                  padding: "16px 20px", border: "none",
                  background: "linear-gradient(135deg,#b45309,#f59e0b)",
                  color: "#431407", fontWeight: 700,
                  boxShadow: "0 4px 14px rgba(245,158,11,0.25)",
                  textAlign: "left",
                }}
              >
                <div className="flex items-center justify-center rounded-lg shrink-0"
                  style={{ width: 42, height: 42, background: "rgba(255,255,255,0.2)" }}>
                  <ClipboardList size={20} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[15px]">{t("bookingModal.sendInquiry")}</div>
                  <div className="text-xs font-normal" style={{ color: "rgba(120,53,15,0.65)" }}>
                    {t("bookingModal.sendInquiryDesc")}
                  </div>
                </div>
              </button>
            </motion.div>
          )}

          {/* Step 2 – Form */}
          {step === "form" && (
            <motion.form
              key="form"
              variants={slideIn}
              initial="hidden"
              animate="show"
              exit="exit"
              onSubmit={handleSubmit}
              className="p-6 space-y-4 max-h-[75vh] overflow-y-auto"
            >

              <div>
                <label className={labelClass}>
                  <User size={14} className="text-amber-600" />
                  {t("bookingModal.fullName")}
                </label>
                <input
                  type="text"
                  placeholder={t("bookingModal.fullNamePlaceholder")}
                  value={form.name}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className={labelClass}>
                  <Smartphone size={14} className="text-amber-600" />
                  {t("bookingModal.contactNumber")}
                </label>
                <input
                  type="tel"
                  placeholder={t("bookingModal.contactNumberPlaceholder")}
                  value={form.contact}
                  maxLength={10}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  className={inputClass}
                />
                {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
              </div>

              <div>
                <label className={labelClass}>
                  <Calendar size={14} className="text-amber-600" />
                  {t("bookingModal.travelMonth")}
                </label>
                <select
                  value={form.month}
                  onChange={(e) => setForm({ ...form, month: e.target.value })}
                  className={inputClass}
                >
                  <option value="">{t("bookingModal.selectMonth")}</option>
                  {MONTH_KEYS.map((key) => (
                    <option key={key} value={key.charAt(0).toUpperCase() + key.slice(1)}>
                      {t(`bookingModal.months.${key}`)}
                    </option>
                  ))}
                </select>
                {errors.month && <p className="text-red-500 text-xs mt-1">{errors.month}</p>}
              </div>

              <div>
                <label className={labelClass}>
                  <Users size={14} className="text-amber-600" />
                  {t("bookingModal.passengers")}
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={form.passengers}
                  onChange={(e) => setForm({ ...form, passengers: e.target.value })}
                  className={inputClass}
                />
                {errors.passengers && <p className="text-red-500 text-xs mt-1">{errors.passengers}</p>}
              </div>

              <div>
                <label className={labelClass}>
                  <Package size={14} className="text-amber-600" />
                  {t("bookingModal.packageService")}
                </label>
                <input
                  type="text"
                  placeholder={t("bookingModal.packageServicePlaceholder")}
                  value={form.packageService}
                  maxLength={150}
                  onChange={(e) => setForm({ ...form, packageService: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep("options")}
                  disabled={sending}
                  className="flex-1 flex items-center justify-center gap-1.5 border-2 border-gray-200 text-gray-600 font-semibold py-2.5 rounded-xl cursor-pointer disabled:opacity-50 smt-btn-back"
                  style={{ background: "white" }}
                >
                  <ArrowLeft size={15} />
                  {t("bookingModal.back")}
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex-1 font-bold py-2.5 rounded-xl cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 smt-btn-primary"
                  style={{
                    border: "none",
                    background: "linear-gradient(135deg,#b45309,#f59e0b)",
                    boxShadow: "0 4px 14px rgba(245,158,11,0.25)",
                    color: "#431407",
                  }}
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t("bookingModal.sending")}
                    </>
                  ) : t("bookingModal.submit")}
                </button>
              </div>

              {sendErr && (
                <p className="text-red-600 text-xs text-center bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {sendErr}
                </p>
              )}
            </motion.form>
          )}

          {/* Step 3 – Success */}
          {step === "success" && (
            <motion.div
              key="success"
              variants={slideIn}
              initial="hidden"
              animate="show"
              exit="exit"
              className="p-8 text-center"
            >
              <div className="inline-flex items-center justify-center rounded-full mx-auto mb-4"
                style={{ width: 72, height: 72, background: "rgba(34,197,94,0.1)" }}>
                <CheckCircle2 size={40} style={{ color: "#22c55e" }} />
              </div>

              <h3 className="font-serif text-2xl font-extrabold text-gray-800 mb-2">
                {t("bookingModal.success.title")}
              </h3>

              <p className="text-gray-500 mb-4">
                {t("bookingModal.success.message", {
                  name: form.name,
                  contact: form.contact,
                })}
              </p>

              <p className="text-sm text-gray-400 mb-6">
                {t("bookingModal.success.orCall")}{" "}
                <a href="tel:+918866077880" className="font-bold" style={{ color: "#b45309" }}>
                  +91 88660 77880
                </a>
              </p>

              <button
                onClick={onClose}
                className="cursor-pointer font-bold px-8 py-3 rounded-full smt-btn-primary"
                style={{
                  border: "none",
                  background: "linear-gradient(135deg,#b45309,#f59e0b)",
                  color: "#431407",
                  boxShadow: "0 4px 14px rgba(245,158,11,0.3)",
                }}
              >
                {t("bookingModal.success.close")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default BookingModal;
